import { Component, OnInit, Signal, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router, RouterLinkActive, RouterLink, RouterOutlet } from '@angular/router';
import { map } from 'rxjs/operators';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { ApiService, EmployeeService, PermissionsService, UtilsService } from 'src/app/services';
import { CompanyProductSettingsDialogComponent } from '../company-product-settings-dialog/company-product-settings-dialog.component';
import { CompanyProductDefaultTimeDistributionCrudDialogComponent } from './company-product-okr-and-metrics/company-product-default-time-distribution-crud-dialog/company-product-default-time-distribution-crud-dialog.component';
import { CompanyProductReportDialogComponent } from './company-product-okr-and-metrics/company-product-report-dialog/company-product-report-dialog.component';

@Component({
  selector: 'app-company-product-detail',
  templateUrl: './company-product-detail.component.html',
  styleUrls: ['./company-product-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTabNav,
    MatTabLink,
    RouterLinkActive,
    RouterLink,
    MatTabNavPanel,
    RouterOutlet,
    MatIconButton,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class CompanyProductDetailComponent implements OnInit {
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly employee = inject(EmployeeService);
  private readonly utils = inject(UtilsService);
  readonly permissions = inject(PermissionsService);

  readonly is_worker = this.permissions.isWorker();
  /** Workers use `:id`; partners/clients use `:company_product_id`. */
  readonly company_product_id = resolveCompanyProductIdFromParamMap(this.activated_route.snapshot.paramMap);
  readonly company_product = toSignal(
    this.api
      .getCompanyProduct(String(this.company_product_id), { with: 'company' })
      .pipe(map(company_product => company_product as CompanyProduct | null)),
    {
      initialValue: null as CompanyProduct | null,
    }
  );

  navLinks: { label: string; link: string; index: number }[] = [];
  activeLinkIndex = 0;
  queryParams: Record<string, unknown> = {};

  readonly has_pending_communication_config = computed((): boolean => {
    return this.employee
      .actionsRequired()
      .pending_bisual_internal_team_slack_channel_config_company_product_ids.includes(this.company_product_id);
  });

  readonly has_pending_bot_not_member = computed((): boolean => {
    return this.employee
      .actionsRequired()
      .pending_bot_not_member_messaging_conversation_company_product_ids.includes(this.company_product_id);
  });

  readonly has_pending_unconfigured_applications = computed((): boolean => {
    return (this.employee.actionsRequired().pending_unconfigured_applications_company_product_ids ?? []).includes(this.company_product_id);
  });

  /** Same fact_name as CompanyRepository::getBisualCompanyName() on the backend. */
  readonly can_download_daily_anonymized_dump = computed((): boolean => {
    const company_product = this.company_product();
    if (company_product?.company == null) {
      return false;
    }

    if (company_product.company.fact_name !== 'Consultoria Bisual SL') {
      return false;
    }

    return this.permissions.isBisualProductionTeamInCompanyProduct(String(this.company_product_id));
  });

  ngOnInit(): void {
    this.setNavLinks();
    this.router.events.subscribe(() => {
      this.activeLinkIndex = this.navLinks.indexOf(
        this.navLinks.find(tab => tab.link === '.' + this.router.url) as {
          label: string;
          link: string;
          index: number;
        }
      );
    });
    this.listenQueryParams();
  }

  startMeetingMode(): void {
    this.employee.startMeetingMode(this.company_product_id);
  }

  openCompanyProductSettings(): void {
    this.dialog.open(CompanyProductSettingsDialogComponent, {
      width: '600px',
      data: { company_product_id: this.company_product_id },
    });
  }

  openTimeDistributionCrudDialog(): void {
    this.dialog.open(CompanyProductDefaultTimeDistributionCrudDialogComponent, {
      width: '1200px',
      data: {
        company_product_id: this.company_product_id,
      },
    });
  }

  openCompanyProductReport(): void {
    const company_product = this.company_product();
    if (company_product === null) {
      return;
    }

    this.dialog.open(CompanyProductReportDialogComponent, {
      width: '650px',
      data: {
        company_product,
      },
    });
  }

  downloadDailyAnonymizedDump(): void {
    this.api.downloadCompanyProductDailyAnonymizedDump(this.company_product_id).subscribe({
      next: (blob): void => {
        this.utils.downloadDocumentFromBlob(blob, 'daily_anonymized_dump.sql');
      },
    });
  }

  private setNavLinks(): void {
    const company_product_id = String(this.company_product_id);
    const can_see_billing_periods =
      this.permissions.isDepartment('direction') ||
      this.permissions.isDepartment('rrhh') ||
      this.permissions.isAccountManagerInCompanyProduct(company_product_id);

    if (this.permissions.isWorker()) {
      this.navLinks = [
        { label: 'OKRs y métricas', link: './okr-and-metrics', index: 0 },
        { label: 'Fases', link: './phases', index: 1 },
        { label: 'Reuniones', link: './meeting-records', index: 2 },
        { label: 'Aplicaciones', link: './applications', index: 3 },
        { label: 'Despliegues', link: './company-product-deploys', index: 4 },
        { label: 'Equipos', link: './company-product-teams', index: 5 },
        { label: 'Guardias', link: './guardian-scheduled-weeks', index: 6 },
      ];

      let nextIndex = 7;

      if (
        this.permissions.isProductionTeamInCompanyProduct(company_product_id) ||
        this.permissions.isAccountManagerInCompanyProduct(company_product_id) ||
        this.permissions.isAdmin()
      ) {
        this.navLinks.push({
          label: 'Monitores',
          link: './status-monitors',
          index: nextIndex++,
        });
      }

      if (this.permissions.isBisualProductionTeamInCompanyProductAsTrio(company_product_id)) {
        this.navLinks.push({
          label: 'Ubicaciones',
          link: './locations',
          index: nextIndex++,
        });
      }

      if (can_see_billing_periods) {
        this.navLinks.push({
          label: 'Facturas y pagos',
          link: './company-product-monthly-economic-reports',
          index: nextIndex++,
        });
      }

      if (this.permissions.isAccountManagerInCompanyProduct(company_product_id)) {
        this.navLinks.push({
          label: 'Comunicación',
          link: './communication',
          index: nextIndex++,
        });
      }
    } else if (this.permissions.isClient() || this.permissions.isPartner()) {
      this.navLinks = [
        { label: 'OKRs y métricas', link: './okr-and-metrics', index: 0 },
        { label: 'Fases', link: './phases', index: 1 },
        { label: 'Despliegues', link: './company-product-deploys', index: 2 },
      ];
    }
  }

  private listenQueryParams(): void {
    this.activated_route.queryParamMap.subscribe(data => {
      const params: Record<string, string> = {};
      data.keys.forEach(key => {
        const value = data.get(key);
        if (value !== null) {
          params[key] = value;
        }
      });

      if (params['from'] != null) {
        delete params['from'];
      }
      if (params['to'] != null) {
        delete params['to'];
      }

      this.queryParams = params;
    });
  }
}

export function injectCompanyProductFromDetail(): Signal<CompanyProduct | null> {
  const parent = inject(CompanyProductDetailComponent, { optional: true, skipSelf: true });
  return parent?.company_product ?? signal(null);
}

/** Route param is `:id` under employees/development and `:company_product_id` under partners/clients. */
export function resolveCompanyProductIdFromParamMap(param_map: ParamMap): number {
  const raw = param_map.get('id') ?? param_map.get('company_product_id');
  return Number(raw);
}
